import React from "react";
import { Helmet } from "react-helmet";
import PageHeader from "../Layouts/PageHeader";
import { UiIcon } from "../Layouts/Icon";
import { ICON_COUNT_TEXT } from "../constants";
import classes from "./license.scss";

const DESCRIPTION = `Download from our extensive collection of ${ICON_COUNT_TEXT} free, high-quality icons including duotone. Ideal for enhancing your designs, websites, mobile apps, and software.`;

// Which license covers which files
const LICENSES = [
  {
    icon: "star",
    files: "Icons",
    name: "CC BY 4.0",
    href: "https://creativecommons.org/licenses/by/4.0/",
  },
  {
    icon: "edit",
    files: "Font files",
    name: "SIL OFL",
    href: "https://scripts.sil.org/OFL",
  },
  {
    icon: "code",
    files: "Everything else in the download",
    name: "MIT",
    href: "https://opensource.org/licenses/MIT",
  },
];

const SECTIONS = [
  { id: "licenses", label: "Licenses" },
  { id: "attribution", label: "Attribution" },
  { id: "brand-icons", label: "Brand icons" },
  { id: "disclaimer", label: "Disclaimer" },
];

const External = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

export const License = () => (
  <section className="main-body">
    <Helmet>
      <title>License | IcoFont</title>
      <meta name="description" content={DESCRIPTION} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://icofont.com/license" />
      <meta property="og:site_name" content="IcoFont" />
      <meta property="og:image" content="https://icofont.com/images/og/icons.png" />
      <meta name="og:title" content="License | IcoFont" />
      <meta name="og:description" content={DESCRIPTION} />
      <meta property="fb:app_id" content="101529056621542" />
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:site" content="IcoFont" />
      <meta name="twitter:description" content={DESCRIPTION} />
      <meta name="twitter:image:src" content="https://icofont.com/images/og/icons.png" />
    </Helmet>
    <PageHeader label="License" title="Free for personal and commercial use" />

    <div className={classes.page}>
      <nav className={classes.toc} aria-label="On this page">
        <span>On this page</span>
        <ul>
          {SECTIONS.map((section) => (
            <li key={section.id}>
              <a href={`#${section.id}`}>{section.label}</a>
            </li>
          ))}
        </ul>
      </nav>

      <article className={classes.body}>
        <p className={classes.lead}>
          IcoFont is an open source initiative: a single font of {ICON_COUNT_TEXT} infinitely
          scalable vector icons, absolutely free. Download it from icofont.com and use it in any
          legal personal, organizational or commercial project.
        </p>

        <section id="licenses" className={classes.section}>
          <h2>Licenses</h2>
          <p>Each part of IcoFont comes under its own license.</p>
          <div className={classes.licenses}>
            {LICENSES.map((license) => (
              <a
                key={license.name}
                className={classes.license}
                href={license.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className={classes.licenseIcon}>
                  <UiIcon name={license.icon} />
                </span>
                <span className={classes.licenseFiles}>{license.files}</span>
                <strong>{license.name}</strong>
                <span className={classes.licenseRead}>
                  Read the license <UiIcon name="arrow-right" />
                </span>
              </a>
            ))}
          </div>
          <p>
            All icons are licensed under the{" "}
            <External href="https://creativecommons.org/licenses/by/4.0/">CC BY 4.0</External>{" "}
            license. The <External href="https://scripts.sil.org/OFL">SIL OFL</External> license
            applies to the font files only. For all the non-font and non-icon files that come with
            IcoFont downloads, the{" "}
            <External href="https://opensource.org/licenses/MIT">MIT license</External> is
            applicable.
          </p>
        </section>

        <section id="attribution" className={classes.section}>
          <h2>Attribution</h2>
          <p>
            Attribution is required by CC BY, SIL OFL, and MIT licenses. Downloaded IcoFont files
            contain embedded comments with necessary attribution. While using these files normally,
            you shouldn't need to do anything additional to provide attribution.
          </p>
          <div className={classes.note}>
            <UiIcon name="heart" />
            <p>
              It is always appreciated if you credit IcoFont with our website address (icofont.com)
              on your project so that visitors/users can know about IcoFont at their first
              visit/use.
            </p>
          </div>
        </section>

        <section id="brand-icons" className={classes.section}>
          <h2>Brand icons</h2>
          <p>
            The brand logos or icons incorporated into the IcoFont project are proprietary
            trademarks belonging to the respective entities. Please be aware that IcoFont does not
            endorse these trademarks nor is it endorsed by them.
          </p>
          <p>
            We emphasize that you are not permitted to utilize any of the brand icons in a manner
            that is inappropriate or could be perceived as misleading. The goal of providing these
            icons is to assist you in creating genuine and compliant representations of these
            brands. Any misuse of these icons can infringe on the rights of the respective
            trademark owners. Please respect these guidelines to maintain a fair and respectful
            environment for all.
          </p>
        </section>

        <section id="disclaimer" className={classes.section}>
          <h2>Disclaimer</h2>
          <p>
            The software is provided “as is”, without any kind of warranty. The IcoFont authority
            will not be liable for any type of claim, liability or damage.
          </p>
          <p>
            The IcoFont authority reserves the right to change or modify the above license terms at
            any time without any prior notice.
          </p>
        </section>

        <p className={classes.updated}>These terms were last modified on June 6, 2023.</p>
      </article>
    </div>
  </section>
);

export default License;
